// src/components/VisitorParking.tsx
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { TextField, Button, Typography, Grid2, Paper, useTheme } from '@mui/material';
import DatePickerComponent from '../CommonComponents/CustomDatePicker';
import { useLocation, useNavigate } from 'react-router-dom';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import { useDispatch, useSelector } from 'react-redux';
import { currentUnit, fetchUnit } from '../../Redux/selectors';
import { Actions } from '../../Redux/Reducer/rootReducer';
import { DirectionsCar, MeetingRoom } from '@mui/icons-material';
import { carBrands, carModels } from '../../Utils/Constants';
import { Unit, UserType, VisitorParkingRequestStatus } from '../../Types/UnitFeatureTypes';
import { useAuth } from '../../context/authContext';
import { validateEmail } from '../../Utils/Utils';

interface VisitorParkingFormData {
  unit_id: string;
  user_id?: string;
  car: {
    make: string;
    model: string;
    plate: string;
    color: string;
  }
  toDate: Date;
  fromData: Date;
  externalEmail?: string;
}

const VisitorParking = () => {
  const [formData, setFormData] = useState<VisitorParkingFormData>({
    unit_id: '',
    user_id: '',
    car: {
      make: '',
      model: '',
      plate: '',
      color: '',
    },
    toDate: new Date(),
    fromData: new Date(),
    externalEmail: '',
  });

  const location = useLocation();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useAuth();

  const unitData = useSelector(fetchUnit); //Remove for auth flow later
  const userUnitData = useSelector(currentUnit) as Unit;
  const IS_MANAGER = user?.type === UserType.MANAGER;
  const IS_SECURITY = user?.type === UserType.SECURITY;
  const theme = useTheme();

  const {reschedule, licensePlate, unit_id, carBrand, carModel, carColor, toDate, fromDate} = location.state || {};


  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(Actions.fetchUnit.unitActions.request({fresh: true}));
    }
  }, []);


  useEffect(() => {
    if (reschedule) {
      setFormData({
        ...formData,
        unit_id: unit_id,
        car: {
          make: carBrand,
          model: carModel,
          plate: licensePlate,
          color: carColor,
        },
        toDate: new Date(toDate),
        fromData: new Date(fromDate),
      });
    }
  }, [reschedule]);

  const handleCarChange = (e: any) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData({
      ...formData,
      car: {
        ...formData.car,
        [name as string]: value,
      },
    });
  }

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.car.plate) errors.plate = 'License Plate is required.';
    if (!formData.fromData) errors.fromDate = 'Visitor Pass Date is required.';
    if (!formData.toDate) errors.toDate = 'Visitor Pass Date is required.';
    if (!formData.car.make) errors.make = 'Car Brand is required.';
    if (!formData.car.model) errors.model = 'Car Model is required.';
    if (!formData.car.color) errors.color = 'Car Color is required.';
    if (formData.fromData > formData.toDate) errors.fromDate = 'From Date must be before To Date.';

    if (!isAuthenticated && !formData.unit_id) {
      errors.unitNumber = 'Unit Number is required';
    }
    if (!isAuthenticated && !formData.externalEmail) {
      errors.externalEmail = 'Email is required';
    }
    if (!isAuthenticated && !validateEmail(formData.externalEmail)) {
      errors.externalEmail = 'Please enter a valid email';
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) return;
    let visitorParkingData = {
      car: {
        make: formData.car.make,
        model: formData.car.model,
        plate: formData.car.plate,
        color: formData.car.color,
      },
      date_requested: {
        start: formData.fromData.toISOString(),
        end: formData.toDate.toISOString(),
      },
      visitor_unit: isAuthenticated && !IS_MANAGER? userUnitData.id?.toString() : formData.unit_id,
      visitor_for: isAuthenticated ? user?.id : null,
      status: !isAuthenticated ? VisitorParkingRequestStatus.PENDING : VisitorParkingRequestStatus.APPROVED,
      external_person_email: formData.externalEmail,
    };
    if (reschedule) {
      dispatch(Actions.visitorParking.updateVisitorParkingRequestAction.request({visitorParkingData: {
        ...visitorParkingData,
        id: location.state.id,
      }}));
    } else {
      dispatch(Actions.visitorParking.createVisitorParkingRequestAction.request({visitorParkingData}));
    }
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <Paper elevation={5} sx={{ padding: 6 }}>
        <Typography variant="h5" gutterBottom>
          Visitor Parking Form
        </Typography>
        {!isAuthenticated && <Typography sx={{mt:1, mb:1.5}} color='warning' variant="body2" gutterBottom>
          Note: The person you are visiting will be notified of your visit. They will need to approve your visit before you can park in the visitor parking.
        </Typography>}
        <form>
          {!isAuthenticated && <TextField
            label="Your email"
            name="externalEmail"
            value={formData.externalEmail}
            onChange={(e) => setFormData({ ...formData, externalEmail: e.target.value })}
            fullWidth
            sx={{ mb: 1.5 }}
            variant="outlined"
            error={!!errors.externalEmail}
            helperText={errors.externalEmail}
          />}
          {(!isAuthenticated || IS_MANAGER || IS_SECURITY) && (
            <DropdownSelection
              dropDownLabel='Unit'
              showHeader={false}
              currentValue={formData.unit_id}
              selectionValues={unitData.map((unit) => ({
                  value: unit.id.toString(),
                  label: unit.unit_number.toString(),
                  color: theme.palette.text.primary,
                  icon: MeetingRoom,
              }))}
              showError={!!errors.unitNumber}
              helperText={errors.unitNumber}
              variant='outlined'
              // sxObject={{ mb: 2, mt: 1.5}}
              handleOnChange={(e) => {setFormData({ ...formData, unit_id: e.target.value }); setErrors({});}}
            />
          )}
        <Grid2 marginTop={2} container direction={'row'} spacing={2} justifyContent={'flex-start'}>
            <Grid2 size={{xs: 6}}>
                <DatePickerComponent 
                    labelValue="From Date"
                    selectedDate={dayjs(formData.fromData)}
                    setSelectedDate={(newDate) => setFormData({ ...formData, fromData: newDate!.toDate() })}
                    minDate={dayjs()}
                />
            </Grid2>
            <Grid2 size={{xs: 6}}>
                <DatePickerComponent 
                    labelValue="To Date"
                    selectedDate={dayjs(formData.toDate)}
                    setSelectedDate={(newDate) => setFormData({ ...formData, toDate: newDate!.toDate() })}
                    minDate={dayjs(formData.fromData)}
                />
            </Grid2>
        </Grid2>

          <DropdownSelection
            dropDownLabel='Car Brand'
            showHeader={false}
            labelSx={{mt:1.5}}
            currentValue={formData.car.make}
            isReadOnly={reschedule}
            selectionValues={carBrands.map((brand) => ({
                value: brand,
                label: brand,
                color: theme.palette.text.primary,
                icon: DirectionsCar,
            }))}
            showError={!!errors.make}
            helperText={errors.make}
            variant='outlined'
            sxObject={{ mt: 1.5}}
            handleOnChange={(e) => {setFormData({ ...formData, car: { ...formData.car, make: e.target.value } }); setErrors({});}}
          />

          {/* Car Model Dropdown */}
          {formData.car.make && 
            <DropdownSelection
              dropDownLabel='Car Model'
              showHeader={false}
              labelSx={{mt:1.5}}
              isReadOnly={reschedule}
              currentValue={formData.car.model}
              selectionValues={carModels[formData.car.make].map((model) => ({
                  value: model,
                  label: model,
                  color: theme.palette.text.primary,
                  icon: DirectionsCar,
              }))}
              showError={!!errors.model}
              helperText={errors.model}
              variant='outlined'
              sxObject={{ mt: 1.5}}
              handleOnChange={(e) => {setFormData({ ...formData, car: { ...formData.car, model: e.target.value } }); setErrors({});}}
            />}

          <TextField
            label="Car Color"
            name="color"
            value={formData.car.color}
            onChange={handleCarChange}
            disabled={reschedule}
            fullWidth
            sx={{ mt: 1.5 }}
            variant="outlined"
            error={!!errors.color}
            helperText={errors.color}
          />

          <TextField
            label="License Plate"
            name="plate"
            value={formData.car.plate}
            disabled={reschedule}
            onChange={(e) => handleCarChange(e as React.ChangeEvent<HTMLInputElement>)}
            fullWidth
            sx={{ mt: 1.5 }}
            variant="outlined"
            error={!!errors.plate}
            helperText={errors.plate}
          />
          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            fullWidth
            sx={{ marginTop: 2 }}
          >
            Submit
          </Button>
        </form>
    </Paper>
  );
};

export default VisitorParking;
